import React from "react";
import {
  Image,
  Navigation,
  Page,
  Storage,
  TaskMultiChoice,
} from "@bittnerdevelopmentgroup/interactive";

import "./styles.css"

import panel1 from "./assets/Int_PrinEco5_1-1.jpg"
import panel4 from "./assets/Int_PrinEco5_1-4_Custom.jpg"


class FiveFoundations extends React.Component {
  constructor(props) {
    super(props)
    this.state = {}
  }

  render() {
    return (
      <div className="fiveFoundations">
        <div className="sr-only" aria-hidden="true">
          {[panel4].map(src => <img alt="" src={src} />)}
        </div>
        <Navigation interactiveId="chapter-1">
          <Page>
            <p>
              Economics is the study of decision-making. Because we all have many
              wants and needs and only limited resources to meet them, economists
              study how we make those choices.
            </p>
            <p>
              If that sounds complicated, it really isn't: in fact, as you go about your
              day, you're probably already making economic decisions!
            </p>
            <p>
              Five major principles influence most decisions that we make. This text
              refers to them as the “five foundations of economics.” Answer the
              following questions to see how your day-to-day choices demonstrate
              each of those foundations.
            </p>
            <p align='center'>
              <Image
                src={panel1}
                width="80%"
                alt="Five Foundations of Economics"
                longAlt={"Five Foundations of Economics. Pictured are five labeled pillars. The incentives pillar has a mouse running on an exercise wheel, chasing cheese. The trade offs pillar has a pair of scales. The opportunity cost pillar has a book and a gaming console on it. The marginal thinking pillar has a cloud labeled plus 1 on it. The trade creates value pillar has a globe with trade routes on it."}
              />
            </p>
          </Page>
          <Page>
            <p>
              Two of your neighbors need someone to look after their kids on
              Wednesday night, and both ask if you're available to babysit. Each
              family has two well-behaved, young kids; they live equal distances
              away from you, and you know both families relatively well. The only
              difference is that one neighbor offers you $12.50 per hour for the job
              and the other offers you $20.00 per hour.
            </p>
            <TaskMultiChoice
              prompt={<div><p><b>Since everything about the two jobs is basically the same, which job would an economist expect you to choose?</b></p><p><b>Select an answer.</b></p></div>}
              key="ffnpanel2"
              style={{ display: "flex", justifyContent: "center" }}
              state={Storage()}
              choices={[
                <span>
                  The job offering $12.50 per hour.
                </span>,
                <span>
                  The job offering $20.00 per hour.
                </span>,
              ]}
              correct={1}
              cols={2}
              justifyChoices="center"
              feedback={[
                <div>
                  Everything about these two jobs is essentially the same, except for the pay rate. Which rate would be more appealing? $12.50 per hour or $20.00 per hour?
                </div>,
                <div>
                  Exactly! The pay is what economists call an <b>incentive</b>, a factor that motivates people to act or exert effort. Economists would expect you to choose the job where the incentive (in this case, the hourly pay) is greater.
                </div>
              ]}
            />
          </Page>
          <Page>
            <p>
              Now that you have made that choice, let's add to the story. You are
              an excellent guitarist, and you spend every weekday evening
              practicing with your band.
            </p>
            <TaskMultiChoice
              prompt={<div><p><b>Will taking the babysitting job leave more or less time for you to practice with your band?</b></p><p><b>Select an answer.</b></p></div>}
              key="ffpanel3"
              className="five-foundations"
              state={Storage()}
              choices={[
                <span>
                  more
                </span>,
                <span>
                  less
                </span>,
              ]}
              correct={1}
              cols={2}
              feedback={[
                <div>
                  This job is on a Wednesday evening, when you usually go to band practice. There are only so many evening hours in the week. If you spend those hours babysitting, you can't use them for band practice.
                </div>,
                <div>
                  <p>
                    Taking the babysitting job will mean giving up band practice time. This is what economists call a <b>trade-off</b>. The amount of time you give up practicing with your band in order to babysit is the <b>opportunity cost</b> of the choice.
                  </p>
                  <p>
                    In this case, if you take the babysitting job, it means that you consider yourself better off because of the decision. You value the money that you receive from babysitting more than those hours of band practice. The couple that hires you feels that they are better off, too. While they have paid money for your babysitting, they value their Wednesday night out more than the $20.00 per hour that they pay.
                  </p>
                  <p>
                    This illustrates how <b>trade creates value</b>. In this situation, you trade your time for money, and the couple trades their money for time. In both cases, this was done because one option was valued more than the other. Taken together, this trade created more value for each than if the trade had not happened.
                  </p>
                </div>
              ]}
            />
          </Page>
          <Page>
            <p>
              This neighbor likes your work and wants to hire you regularly during the week. The couple asks you to work 10–20 hours per week, but they let you decide exactly how many hours you'll babysit. You could work all 20, but you decide to work 16 hours and take the rest as free time to practice your guitar.
            </p>
            <p align='center'>
              <Image
                src={panel4}
                width="80%"
                alt="A young girl and her babysitter sit together at a coffee table and draw with colored pens and pencils."
                photoCredit="Shutterstock/BAZA Production"
              />
            </p>
            <TaskMultiChoice
              prompt={<div><p><b>If the job pays $20.00 per hour, which fact matters most for your decision to work 16 hours?</b></p><p><b>Select an answer.</b></p></div>}
              key="ffpanel4"
              state={Storage()}
              choices={[
                <span>
                  The other couple had offered $12.50 per hour, so $20.00 per hour is a very good wage.
                </span>,
                <span>
                  Each added hour that you work pays another $20.00.
                </span>,
                <span>
                  The total pay for 16 hours is $320.00.
                </span>,
              ]}
              correct={1}
              feedback={[
                <div>
                  You could have chosen 15 hours or 17 hours. Which factor in this decision would have led you to choose 16?
                </div>,
                <div>
                  Exactly! Deciding whether to work 15, 16, or 17 hours requires <b>marginal thinking</b>. You have to decide whether each additional hour of band practice time lost is worth the $20.00 you earn. In this case, you decide that 16 hours is worthwhile, but the seventeenth hour would not be.
                </div>,
                <div>
                  You could have chosen 15 hours or 17 hours. Which factor in this decision would have led you to choose 16?
                </div>,
              ]}
            />
          </Page>
          <Page>
            <p>
              Great work! As you can see, you already have experience applying this
              text's five foundations of economics. In making decisions, people use
              the economic concepts of:
            </p>
            <ul>
              <li>Incentives</li>
              <li>Trade-offs</li>
              <li>Opportunity cost</li>
              <li>Marginal thinking</li>
              <li>Trade creates value</li>
            </ul>
          </Page>
        </Navigation>
      </div>
    )
  }
}

export default FiveFoundations
